import React, { useEffect, useState } from "react";
import {
    PhoneCall,
    Volume2,
    VolumeX,
    Wind,
    Activity,
    HeartPulse,
} from "lucide-react";
import { speak, cancel as cancelSpeech, isMuted, setMuted } from "../lib/speech";
import { useI18n } from "../lib/i18n";
import { useQr } from "../lib/qrContext";
import { useEmergencyNumber } from "../lib/useEmergencyNumber";

const BREADCRUMB_KEYS = {
    "consciousness:no": "breadcrumb_conscious_no",
    "consciousness:yes": "breadcrumb_conscious_yes",
    "breathing:no": "breadcrumb_breathing_no",
    "breathing:yes": "breadcrumb_breathing_yes",
    "called112": "breadcrumb_called_112",
};

export const ConsciousnessTriage = ({
    mode,
    onStartCpr,
    decisions = [],
    setDecisions,
}) => {
    const { t, lang } = useI18n();
    const { custom } = useQr();
    const [step, setStep] = useState("consciousness");
    const [muted, setMutedState] = useState(isMuted());

    useEffect(() => {
        if (muted) return undefined;
        const textKey = {
            "consciousness": "triage_consciousness_tts",
            "advice-conscious": "triage_advice_conscious_tts",
            "breathing": "triage_breathing_tts",
            "advice-breathing": "triage_advice_breathing_tts",
            "advice-no-breath": "triage_advice_no_breath_tts",
        }[step];
        if (textKey) {
            const base = t(textKey);
            // For the urgent post-triage screen we append the location so
            // the responder is reminded where they are while dialling.
            const withLocation =
                step === "advice-no-breath" &&
                (custom?.title || custom?.location)
                    ? `${base} ${t("urgent_location_tts")
                          .replace("{title}", custom?.title || "")
                          .replace("{location}", custom?.location || "")
                          .trim()}`
                    : base;
            const prefixed =
                mode === "demo"
                    ? `${t("demo_tts_prefix")} ${withLocation}`
                    : withLocation;
            speak(prefixed, { repeat: true, repeatDelay: 3000 });
        }
        return () => cancelSpeech();
    }, [step, muted, lang, t, mode, custom]);

    const toggleMute = () => {
        const next = !muted;
        setMuted(next);
        setMutedState(next);
        if (next) cancelSpeech();
    };

    const addDecision = (key) => {
        setDecisions?.((d) => (d.includes(key) ? d : [...d, key]));
    };

    return (
        <section
            data-testid="triage-section"
            className="relative bg-white border-2 border-zinc-200 rounded-3xl p-6 sm:p-10 shadow-sm anim-fade-up"
        >
            {decisions.length > 0 && <Breadcrumb decisions={decisions} />}

            {/* Floating, low-key mute toggle. The big question or advice is
                always the title — no redundant section header. */}
            <button
                data-testid="tts-mute-toggle"
                onClick={toggleMute}
                aria-label={muted ? t("tts_activate") : t("tts_mute")}
                className="absolute top-4 right-4 sm:top-5 sm:right-5 w-9 h-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center transition z-10"
            >
                {muted ? (
                    <VolumeX className="w-4 h-4 text-zinc-600" />
                ) : (
                    <Volume2 className="w-4 h-4 text-zinc-900" />
                )}
            </button>

            {step === "consciousness" && (
                <QuestionBlock
                    testId="question-consciousness"
                    icon={<Activity className="w-5 h-5" />}
                    title={t("triage_consciousness_title")}
                    subtitle={t("triage_consciousness_sub")}
                    onYes={() => {
                        addDecision("consciousness:yes");
                        setStep("advice-conscious");
                    }}
                    onNo={() => {
                        addDecision("consciousness:no");
                        setStep("breathing");
                    }}
                />
            )}

            {step === "advice-conscious" && (
                <AdviceBlock
                    testId="advice-conscious"
                    title={t("triage_advice_conscious_title")}
                    body={t("triage_advice_conscious_body")}
                    mode={mode}
                />
            )}

            {step === "breathing" && (
                <QuestionBlock
                    testId="question-breathing"
                    icon={<Wind className="w-5 h-5" />}
                    title={t("triage_breathing_title")}
                    subtitle={t("triage_breathing_sub")}
                    onYes={() => {
                        addDecision("breathing:yes");
                        setStep("advice-breathing");
                    }}
                    onNo={() => {
                        addDecision("breathing:no");
                        setStep("advice-no-breath");
                    }}
                />
            )}

            {step === "advice-breathing" && (
                <AdviceBlock
                    testId="advice-breathing"
                    title={t("triage_advice_breathing_title")}
                    body={t("triage_advice_breathing_body")}
                    mode={mode}
                />
            )}

            {step === "advice-no-breath" && (
                <AdviceBlock
                    testId="advice-no-breath"
                    title={t("triage_advice_no_breath_title")}
                    body={t("triage_advice_no_breath_body")}
                    mode={mode}
                    urgent
                    onCpr={() => {
                        addDecision("called112");
                        onStartCpr?.();
                    }}
                />
            )}
        </section>
    );
};

const Breadcrumb = ({ decisions }) => {
    const { t } = useI18n();
    return (
        <div
            data-testid="triage-breadcrumb"
            className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] sm:text-xs font-display font-bold uppercase tracking-[0.18em] text-zinc-400"
        >
            {decisions.map((d, i) => (
                <React.Fragment key={d}>
                    {i > 0 && <span className="text-zinc-300">/</span>}
                    <span data-testid={`breadcrumb-${d}`} className="text-zinc-600">
                        {BREADCRUMB_KEYS[d] ? t(BREADCRUMB_KEYS[d]) : d}
                    </span>
                </React.Fragment>
            ))}
        </div>
    );
};

const QuestionBlock = ({ testId, icon, title, subtitle, onYes, onNo }) => {
    const { t } = useI18n();
    return (
        <div data-testid={testId} className="anim-fade-up">
            <div className="text-xs font-display font-bold uppercase tracking-widest text-zinc-400 flex items-center gap-2">
                {icon}
                {t("triage_question_label")}
            </div>
            <h3 className="font-display font-black text-3xl sm:text-5xl tracking-tighter leading-[1.05] mt-2">
                {title}
            </h3>
            {subtitle && (
                <p className="text-sm sm:text-base text-zinc-500 mt-2">{subtitle}</p>
            )}

            <div className="mt-6 grid grid-cols-1 gap-3 sm:gap-4">
                <button
                    data-testid={`${testId}-no`}
                    onClick={onNo}
                    className="min-h-[120px] sm:min-h-[140px] rounded-3xl bg-[#A72920] text-white font-display font-black text-3xl sm:text-4xl tracking-tight hover:bg-[#8e2019] active:scale-[0.98] transition-all focus-visible:ring-4 focus-visible:ring-[#A72920]/30"
                >
                    {t("triage_no")}
                </button>
                <button
                    data-testid={`${testId}-yes`}
                    onClick={onYes}
                    className="min-h-[120px] sm:min-h-[140px] rounded-3xl bg-white border-2 border-zinc-300 text-zinc-900 font-display font-black text-3xl sm:text-4xl tracking-tight hover:border-zinc-900 hover:bg-zinc-50 active:scale-[0.98] transition-all focus-visible:ring-4 focus-visible:ring-zinc-900/20"
                >
                    {t("triage_yes")}
                </button>
            </div>
        </div>
    );
};

const AdviceBlock = ({ testId, title, body, urgent, mode, onCpr }) => {
    const { t } = useI18n();
    return (
        <div data-testid={testId} className="anim-fade-up">
            {!urgent && (
                <div className="text-xs font-display font-bold uppercase tracking-widest text-zinc-400">
                    {t("triage_advice_label")}
                </div>
            )}
            <h3
                className={`font-display font-black text-3xl sm:text-5xl tracking-tighter leading-[1.05] ${
                    urgent ? "text-[#A72920]" : "text-zinc-900 mt-2"
                }`}
            >
                {title}
            </h3>
            {!urgent && body && (
                <p className="text-base sm:text-lg text-zinc-600 mt-3 max-w-2xl leading-relaxed">
                    {body}
                </p>
            )}

            <div className="mt-8">
                <Call112Button mode={mode} urgent={urgent} onCpr={onCpr} />
                {urgent && <LocationHint />}
                {urgent && <AedHint />}
                <EmergencyHint />
            </div>
        </div>
    );
};

const LocationHint = () => {
    const { t } = useI18n();
    const { custom } = useQr();
    const title = (custom?.title || "").trim();
    const location = (custom?.location || "").trim();
    if (!title && !location) return null;
    return (
        <div
            data-testid="urgent-location-hint"
            className="mt-4 sm:mt-5 rounded-2xl bg-zinc-900 text-white px-4 sm:px-5 py-3 sm:py-4 text-center"
        >
            <div className="text-[11px] font-display font-bold uppercase tracking-[0.18em] text-white/70">
                {t("urgent_location_label")}
            </div>
            {title && (
                <div className="mt-0.5 font-display font-black text-xl sm:text-2xl leading-tight break-words">
                    {title}
                </div>
            )}
            {location && (
                <div className="mt-1 font-display font-bold text-sm sm:text-base text-white/95 break-words">
                    {location}
                </div>
            )}
        </div>
    );
};

const EmergencyHint = () => {
    const { t } = useI18n();
    const { emergencyHint } = useEmergencyNumber();
    if (!emergencyHint) return null;
    // Renders a small reassurance sentence below the 112 button telling
    // users in CH / UK that 112 is the same as 144 / 999. Suppressed
    // automatically when a custom per-location number is in play.
    return (
        <div
            data-testid="emergency-hint"
            className="mt-3 sm:mt-4 text-center text-xs sm:text-sm text-zinc-600 font-display font-medium leading-relaxed"
        >
            {t(`emergency_hint_${emergencyHint}`)}
        </div>
    );
};

const AedHint = () => {
    const { t } = useI18n();
    const { custom } = useQr();
    const aed = (custom?.aed || "").trim();
    if (!aed) return null;
    return (
        <div
            data-testid="aed-hint"
            className="mt-4 sm:mt-5 flex items-start gap-3 rounded-2xl bg-[#0F8040]/10 border-2 border-[#0F8040]/30 px-4 sm:px-5 py-3 sm:py-4"
        >
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#0F8040] text-white flex items-center justify-center">
                <HeartPulse className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
                <div className="text-[11px] font-display font-bold uppercase tracking-[0.18em] text-[#0F8040]">
                    {t("triage_aed_label")}
                </div>
                <div className="font-display font-black text-base sm:text-lg text-zinc-900 leading-snug mt-0.5 break-words">
                    {aed}
                </div>
            </div>
        </div>
    );
};

const Call112Button = ({ mode, urgent, onCpr }) => {
    const { t } = useI18n();
    const { number, isCustom } = useEmergencyNumber();
    const isDemo = mode === "demo";

    const handleClick = (e) => {
        if (isDemo) {
            e.preventDefault();
            if (urgent && onCpr) {
                onCpr();
            } else {
                window.alert(t("triage_demo_alert_generic"));
            }
            return;
        }
        if (urgent && onCpr) setTimeout(() => onCpr(), 0);
    };

    // For custom per-location numbers we render the actual digits inside
    // the button so the responder knows exactly what will be dialled.
    // For the default 112 case we keep the localised "Notruf 112 wählen"
    // phrasing that already contains {num} → "112".
    const label = isDemo
        ? `${number} (${t("triage_demo_badge")})`
        : isCustom
          ? `${t("triage_emergency_call_custom")} ${number}`
          : t("triage_emergency_call");

    // Custom numbers (e.g. "0611 957820") are longer than "112" – scale
    // the label down adaptively so it always fits inside the button. We
    // pick a size bucket based on the effective label length, then let
    // CSS truncation handle any remaining overflow gracefully.
    const labelLen = label.length;
    let sizeClass = "text-5xl sm:text-7xl";
    if (labelLen > 22) sizeClass = "text-2xl sm:text-4xl";
    else if (labelLen > 16) sizeClass = "text-3xl sm:text-5xl";
    else if (labelLen > 12) sizeClass = "text-4xl sm:text-6xl";

    return (
        <a
            data-testid="call-112-big-button"
            href={isDemo ? "#demo" : `tel:${number.replace(/\s+/g, "")}`}
            onClick={handleClick}
            className={`group relative flex items-center justify-center gap-3 sm:gap-4 min-h-[120px] sm:min-h-[140px] rounded-3xl text-white font-display font-black ${sizeClass} tracking-tighter bg-[#c70000] hover:bg-[#a30000] active:scale-[0.98] transition-all focus-visible:ring-4 focus-visible:ring-[#c70000]/30 shadow-[0_20px_60px_-20px_rgba(199,0,0,0.6)] px-4 ${
                urgent ? "urgent-blink" : ""
            }`}
        >
            <PhoneCall
                className="w-8 h-8 sm:w-12 sm:h-12 flex-shrink-0 transition-transform group-hover:rotate-12"
                strokeWidth={2.5}
            />
            <span className="min-w-0 break-words">{label}</span>
        </a>
    );
};
